import { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Button,
    LinearProgress,
    CircularProgress,
    Divider,
    Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
    useGetResearchDocDocumentsQuery,
    useAddResearchDocDocumentsMutation,
    useRemoveResearchDocDocumentMutation,
    useUploadStorageFileMutation,
    apiSlice
} from 'store/slices/apiSlice';
import { openSnackbar } from 'store/slices/snackbar';

const POLL_INTERVAL_MS = 2000;
const TERMINAL_STATES = ['SUCCESS', 'FAILURE'];

/** RTK Query initiate() trả về action: payload chứa body API, có thể có data nếu baseQuery wrap. */
function getTaskStatusData(action) {
    const payload = action?.payload;
    if (payload && typeof payload === 'object' && ('status' in payload || 'current' in payload)) return payload;
    return action?.data ?? payload?.data;
}

const ACCEPT = '.pdf,.txt,.md,.rst,.docx,.xlsx,.zip';

function safeSnackbar(dispatch, payload) {
    if (typeof openSnackbar === 'function') {
        dispatch(openSnackbar(payload));
    }
}

export default function KnowledgeDocsManager({ open, onClose, knowledge, projectId }) {
    const dispatch = useDispatch();
    const collectionId = knowledge?.id;
    const enabled = open && !!collectionId;
    const { data: docs = [], isLoading } = useGetResearchDocDocumentsQuery(collectionId, { skip: !enabled });
    const [addDocs, { isLoading: isAdding }] = useAddResearchDocDocumentsMutation();
    const [removeDoc] = useRemoveResearchDocDocumentMutation();
    const [uploadFile] = useUploadStorageFileMutation();
    const [deletingId, setDeletingId] = useState(null);
    const fileInputRef = useRef(null);
    const [ingestionProgress, setIngestionProgress] = useState({ active: false, taskIds: [], progress: 0, error: null });

    const handleUploadClick = () => fileInputRef.current?.click();

    useEffect(() => {
        if (!ingestionProgress.active || !ingestionProgress.taskIds?.length || !collectionId) return;
        let cancelled = false;
        const taskIds = ingestionProgress.taskIds;
        const poll = async () => {
            const promises = taskIds.map((taskId) =>
                dispatch(apiSlice.endpoints.getResearchDocTaskStatus.initiate(taskId, { forceRefetch: true }))
            );
            const actions = await Promise.all(promises);
            const results = actions.map(getTaskStatusData);
            if (cancelled) return;
            const allDone = results.every((r) => r && TERMINAL_STATES.includes(r.status));
            const first = results[0];
            const current = first?.current ?? (allDone ? 100 : 0);
            setIngestionProgress((prev) => ({ ...prev, progress: current }));
            if (allDone) {
                const failed = results.filter((r) => r && r.status === 'FAILURE');
                setIngestionProgress((prev) => ({ ...prev, active: false, taskIds: [], progress: 100 }));
                dispatch(apiSlice.util.invalidateTags([{ type: 'ResearchDocs', id: `documents-${collectionId}` }]));
                if (failed.length) {
                    safeSnackbar(dispatch, { open: true, message: failed[0]?.error || 'Một số tài liệu xử lý thất bại.', variant: 'alert', alert: { color: 'error' } });
                } else {
                    safeSnackbar(dispatch, { open: true, message: 'Đã xử lý embedding xong.', variant: 'alert', alert: { color: 'success' } });
                }
                return;
            }
            setTimeout(poll, POLL_INTERVAL_MS);
        };
        poll();
        return () => { cancelled = true; };
    }, [ingestionProgress.active, ingestionProgress.taskIds, collectionId, dispatch]);

    const handleFileChange = async (e) => {
        const files = e.target.files;
        if (!files?.length || !collectionId) return;
        const fileIds = [];
        try {
            for (let i = 0; i < files.length; i++) {
                const res = await uploadFile({ file: files[i], project_id: projectId || undefined }).unwrap();
                const id = res?.id ?? res?.file_id ?? res?.data?.id;
                if (id) fileIds.push(String(id));
            }
            if (fileIds.length > 0) {
                const result = await addDocs({ collectionId, file_ids: fileIds }).unwrap();
                if (result?.task_ids?.length) {
                    setIngestionProgress({ active: true, taskIds: result.task_ids, progress: 0, error: null });
                } else {
                    safeSnackbar(dispatch, { open: true, message: result?.message || 'Đã thêm tài liệu. Đang xử lý...', variant: 'alert', alert: { color: 'success' } });
                }
            }
        } catch (err) {
            safeSnackbar(dispatch, { open: true, message: err?.data?.detail || 'Thêm tài liệu thất bại.', variant: 'alert', alert: { color: 'error' } });
        }
        e.target.value = '';
    };

    const handleRemove = async (doc) => {
        if (!doc?.id) return;
        setDeletingId(doc.id);
        try {
            await removeDoc({ collectionId, documentId: doc.id }).unwrap();
            safeSnackbar(dispatch, { open: true, message: 'Đã xóa tài liệu.', variant: 'alert', alert: { color: 'success' } });
        } catch (err) {
            safeSnackbar(dispatch, { open: true, message: err?.data?.detail || 'Xóa thất bại.', variant: 'alert', alert: { color: 'error' } });
        }
        setDeletingId(null);
    };

    return (
        <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 400 } } }}>
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6">Tài liệu trong Knowledge</Typography>
                <IconButton onClick={onClose} size="small" aria-label="đóng">
                    <CloseIcon />
                </IconButton>
            </Box>
            {knowledge && (
                <Box sx={{ px: 2, py: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        <strong>{knowledge.name}</strong> · {knowledge.document_count ?? 0} tài liệu
                    </Typography>
                </Box>
            )}
            <Divider />
            <Box sx={{ px: 2, py: 1.5 }}>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPT}
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                />
                <Button fullWidth variant="outlined" startIcon={<UploadFileIcon />} onClick={handleUploadClick} disabled={!collectionId || isAdding || ingestionProgress.active}>
                    Thêm tài liệu (upload → embedding)
                </Button>
                {isAdding && <LinearProgress sx={{ mt: 1 }} />}
                {ingestionProgress.active && (
                    <Box sx={{ mt: 1 }}>
                        <LinearProgress variant="determinate" value={ingestionProgress.progress} sx={{ mb: 0.5 }} />
                        <Typography variant="caption" color="text.secondary">Đang embedding... {ingestionProgress.progress}%</Typography>
                    </Box>
                )}
            </Box>
            <Divider />
            <Box sx={{ flex: 1, overflow: 'auto' }}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4, gap: 2 }}>
                        <CircularProgress size={32} thickness={4} />
                        <Typography variant="body2" color="text.secondary">Đang tải tài liệu...</Typography>
                    </Box>
                ) : !docs.length ? (
                    <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                        <Typography variant="body2">Chưa có tài liệu. Thêm file để RAG embedding tự chạy nền.</Typography>
                    </Box>
                ) : (
                    <List dense>
                        {docs.map((doc) => (
                            <ListItem key={doc.id}>
                                <ListItemText
                                    primary={doc.file_name}
                                    secondaryTypographyProps={{ component: 'span' }}
                                    secondary={
                                        <>
                                            {doc.file_size ? `${(doc.file_size / 1024).toFixed(1)} KB` : ''}
                                            {doc.embedding_status && (
                                                <Chip
                                                    size="small"
                                                    label={doc.embedding_status}
                                                    sx={{ ml: 0.5 }}
                                                    color={doc.embedding_status === 'completed' ? 'success' : 'default'}
                                                />
                                            )}
                                        </>
                                    }
                                />
                                <ListItemSecondaryAction>
                                    <IconButton edge="end" size="small" onClick={() => handleRemove(doc)} disabled={deletingId === doc.id} aria-label="xóa">
                                        <DeleteOutlineIcon />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                )}
            </Box>
        </Drawer>
    );
}
